import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('contextmenu', this.#handleContextMenu);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('contextmenu', this.#handleContextMenu);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Shows a custom context menu so users can open the product in a new tab with right-click.
   * @param {MouseEvent} event
   */
  #handleContextMenu = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't override default menus for interactive children (variant pickers, buttons, etc.)
    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) return;

    const link = this.refs.productCardLink;
    if (!link?.href) return;

    event.preventDefault();
    ProductCardContextMenu.instance.open({
      x: event.clientX,
      y: event.clientY,
      url: link.href,
      productTitle: link.textContent?.trim() || link.getAttribute('aria-label') || '',
    });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      history.replaceState({}, '', url.toString());
    }

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

class ProductCardContextMenu {
  static #instance;

  static get instance() {
    if (!this.#instance) this.#instance = new ProductCardContextMenu();
    return this.#instance;
  }

  /** @type {HTMLDivElement} */
  #menu;
  #currentUrl = null;

  constructor() {
    this.#menu = document.createElement('div');
    this.#menu.className = 'product-card-context-menu';
    this.#menu.innerHTML = `
      <button type="button" class="product-card-context-menu__item" data-product-card-menu-action="tab">
        Open product in new tab
      </button>
      <button type="button" class="product-card-context-menu__item" data-product-card-menu-action="window">
        Open product in new window
      </button>
      <button type="button" class="product-card-context-menu__item" data-product-card-menu-action="copy">
        Copy product link
      </button>
    `;
    this.#menu.setAttribute('data-visible', 'false');

    const mountMenu = () => {
      document.body.appendChild(this.#menu);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', mountMenu, { once: true });
    } else if (document.body) {
      mountMenu();
    }

    this.#menu.addEventListener('click', this.#handleMenuClick);
    document.addEventListener('click', this.#handleDocumentClick, { capture: true });
    document.addEventListener('keydown', this.#handleKeydown);
    window.addEventListener('resize', this.hide);
    window.addEventListener('scroll', this.hide, true);
  }

  /**
   * Opens the context menu near the pointer coordinates.
   * @param {{ x: number, y: number, url: string, productTitle?: string }} options
   */
  open({ x, y, url }) {
    this.#currentUrl = url;

    this.#menu.style.visibility = 'hidden';
    this.#menu.dataset.visible = 'true';

    requestAnimationFrame(() => {
      const rect = this.#menu.getBoundingClientRect();
      const padding = 12;
      let left = x;
      let top = y;

      if (left + rect.width + padding > window.innerWidth) {
        left = Math.max(padding, window.innerWidth - rect.width - padding);
      }

      if (top + rect.height + padding > window.innerHeight) {
        top = Math.max(padding, window.innerHeight - rect.height - padding);
      }

      this.#menu.style.left = `${left}px`;
      this.#menu.style.top = `${top}px`;
      this.#menu.style.visibility = 'visible';
    });
  }

  hide = () => {
    this.#currentUrl = null;
    this.#menu.dataset.visible = 'false';
    this.#menu.style.visibility = '';
  };

  #handleMenuClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-product-card-menu-action]') : null;
    if (!(button instanceof HTMLElement) || !this.#currentUrl) return;

    event.preventDefault();
    event.stopPropagation();

    const action = button.getAttribute('data-product-card-menu-action');
    switch (action) {
      case 'tab':
        window.open(this.#currentUrl, '_blank', 'noopener');
        break;
      case 'window':
        window.open(this.#currentUrl, '_blank', 'noopener,noreferrer');
        break;
      case 'copy':
        this.#copyLink();
        break;
      default:
        break;
    }

    this.hide();
  };

  #handleDocumentClick = (event) => {
    if (event.target instanceof Node && this.#menu.contains(event.target)) return;
    this.hide();
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.hide();
    }
  };

  async #copyLink() {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(this.#currentUrl || '');
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = this.#currentUrl || '';
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
    } catch (_) {
      // no-op
    }
  }
}

// Product-card Buy Now:
// - multi-variant (.gokwik-buy-now--quick-add): open quick view ONLY (no cart / GoKwik)
// - single-variant (.gokwik-buy-now): GoKwik with that card's variant id
document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {Element} */ (event.target instanceof Element ? event.target : null);
    if (!target) return;

    // Never hijack clicks inside the quick-view modal (Add to cart / Buy Now handled separately)
    if (target.closest('#quick-add-dialog, quick-add-dialog, .quick-add-modal')) return;

    // Ignore normal quick-add icon/button — that has its own handler
    if (target.closest('quick-add-component .quick-add__button')) return;

    const openQuickViewBtn = target.closest('.gokwik-buy-now--quick-add');
    const buyNowButton = target.closest('.gokwik-buy-now, #gokwik-buy-now');
    const cardButton = openQuickViewBtn || buyNowButton;
    if (!cardButton) return;

    const productCard = cardButton.closest('product-card');
    if (!productCard) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const available = productCard.getAttribute('data-product-available') !== 'false';
    if (!available) {
      if (cardButton instanceof HTMLButtonElement) {
        cardButton.classList.add('disabled');
        cardButton.setAttribute('disabled', 'disabled');
      }
      return;
    }

    const variantsSize = parseInt(productCard.getAttribute('data-product-variants-size') || '0', 10);
    const shouldOpenQuickView =
      Boolean(openQuickViewBtn) || variantsSize > 1 || cardButton.classList.contains('gokwik-buy-now--quick-add');

    if (shouldOpenQuickView) {
      const quickAddEl = productCard.querySelector('quick-add-component');
      const quickAddAny = /** @type {any} */ (quickAddEl);

      if (quickAddAny && typeof quickAddAny.openModalOnly === 'function') {
        quickAddAny.openModalOnly();
      } else if (quickAddAny && typeof quickAddAny.handleClick === 'function') {
        quickAddAny.handleClick(new MouseEvent('click', { bubbles: false, cancelable: true }));
      } else {
        const link = productCard.querySelector('a[ref="productCardLink"]');
        if (link instanceof HTMLAnchorElement) window.location.href = link.href;
      }
      return;
    }

    const firstVariantId = productCard.getAttribute('data-first-variant-id');
    const w = /** @type {any} */ (window);
    if (firstVariantId && typeof w.onBuyNowClick === 'function') {
      if (cardButton instanceof HTMLButtonElement) {
        cardButton.classList.add('disabled');
        cardButton.setAttribute('disabled', 'disabled');
      }
      try {
        w.onBuyNowClick(cardButton, false, [{ id: firstVariantId, quantity: 1 }]);
      } catch (_) {
        // no-op
      }
    }
  },
  true
);

// Quick-view modal Buy Now only — never intercept Add to cart
document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {Element} */ (event.target instanceof Element ? event.target : null);
    if (!target) return;

    // Explicitly ignore add-to-cart / submit controls in the modal
    if (target.closest('button[name="add"], button[type="submit"], add-to-cart-component, .add-to-cart__button')) {
      return;
    }

    const inModalButton = target.closest(
      '#quick-add-dialog .gokwik-buy-now, #quick-add-dialog #gokwik-buy-now, .quick-add-modal .gokwik-buy-now, .quick-add-modal #gokwik-buy-now'
    );
    if (!inModalButton) return;

    if (!inModalButton.closest('#quick-add-dialog, quick-add-dialog, .quick-add-modal')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const modalContent = document.querySelector('#quick-add-modal-content');
    const form = modalContent?.querySelector('form.shopify-product-form');
    const variantInput = /** @type {HTMLInputElement|null} */ (
      form?.querySelector('input[name="id"]:not([disabled]), input[name="id"]')
    );
    const qtyInput = /** @type {HTMLInputElement|null} */ (form?.querySelector('input[name="quantity"]'));
    const variantId = variantInput?.value;
    const quantity = parseInt(qtyInput?.value || '1', 10) || 1;

    const w = /** @type {any} */ (window);
    if (!variantId) return;
    if (typeof w.onBuyNowClick !== 'function') return;

    if (inModalButton instanceof HTMLButtonElement) {
      inModalButton.classList.add('disabled');
      inModalButton.setAttribute('disabled', 'disabled');
    }

    try {
      w.onBuyNowClick(inModalButton, false, [{ id: variantId, quantity }]);
    } catch (_) {
      // no-op
    }

    const dialogEl = document.getElementById('quick-add-dialog');
    const dialogAny = /** @type {any} */ (dialogEl);
    if (dialogAny && typeof dialogAny.closeDialog === 'function') {
      setTimeout(() => {
        try {
          dialogAny.closeDialog();
        } catch (_) {
          // no-op
        }
      }, 200);
    }
  },
  true
);

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
